import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  FormatBoldOutlined,
  FormatItalicOutlined,
  FormatSizeOutlined,
  OpenWithOutlined,
  TextFieldsOutlined,
} from '@mui/icons-material';
import {
  BannerElementLayout,
  BannerElementStyles,
  ElementPosition,
  ElementStyle,
} from '../types/BannerAdmin';
import {
  BANNER_CORNER_RADIUS,
  BANNER_DESIGN_HEIGHT,
  BANNER_DESIGN_WIDTH,
  BannerElementKey,
  BannerPreviewData,
  DEFAULT_LAYOUT,
  resolveStyle,
} from './BannerPreview';

const FONT_OPTIONS = [
  { label: 'Default', value: '' },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Montserrat', value: 'Montserrat, sans-serif' },
  { label: 'Oswald', value: 'Oswald, sans-serif' },
  { label: 'Playfair Display', value: 'Playfair Display, serif' },
  { label: 'Lora', value: 'Lora, serif' },
  { label: 'Poppins', value: 'Poppins, sans-serif' },
  { label: 'Raleway', value: 'Raleway, sans-serif' },
  { label: 'Bebas Neue', value: 'Bebas Neue, sans-serif' },
  { label: 'Courier Prime', value: 'Courier Prime, monospace' },
];

const TRANSFORM_OPTIONS: { label: string; value: ElementStyle['textTransform'] }[] = [
  { label: 'Aa', value: 'none' },
  { label: 'AA', value: 'uppercase' },
  { label: 'aa', value: 'lowercase' },
  { label: 'Ab', value: 'capitalize' },
];

type Props = {
  data: BannerPreviewData;
  layout: BannerElementLayout;
  onLayoutChange: (layout: BannerElementLayout) => void;
  elementStyles: BannerElementStyles;
  onElementStylesChange: (styles: BannerElementStyles) => void;
  aspectRatio?: number;
};

const ELEMENT_LABELS: Record<BannerElementKey, string> = {
  tag: 'Tag',
  title: 'Title',
  subtitle: 'Subtitle',
  cta: 'Button',
};

type DragHandleProps = {
  elementKey: BannerElementKey;
  position: ElementPosition;
  maxWidthPct: number;
  isDragging: boolean;
  isSelected: boolean;
  isResizing: boolean;
  hovering: boolean;
  onPointerDown: (key: BannerElementKey, e: React.PointerEvent) => void;
  onResizePointerDown: (key: BannerElementKey, e: React.PointerEvent) => void;
  onClick: (key: BannerElementKey) => void;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  children: React.ReactNode;
};

const DragHandle: FC<DragHandleProps> = ({
  elementKey,
  position,
  maxWidthPct,
  isDragging,
  isSelected,
  isResizing,
  hovering,
  onPointerDown,
  onResizePointerDown,
  onClick,
  onPointerEnter,
  onPointerLeave,
  children,
}) => {
  const theme = useTheme();
  const showOutline = isDragging || isSelected || hovering || isResizing;
  const movedRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });

  return (
    <Box
      onPointerDown={(e) => {
        movedRef.current = false;
        startPosRef.current = { x: e.clientX, y: e.clientY };
        onPointerDown(elementKey, e);
      }}
      onPointerMove={(e) => {
        if (!movedRef.current) {
          const dx = e.clientX - startPosRef.current.x;
          const dy = e.clientY - startPosRef.current.y;
          if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
            movedRef.current = true;
          }
        }
      }}
      onPointerUp={() => {
        if (!movedRef.current) onClick(elementKey);
      }}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      sx={{
        position: 'absolute',
        left: `${position.x}%`,
        top: `${position.y}%`,
        zIndex: isDragging || isResizing ? 10 : isSelected ? 8 : 2,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        width: `${maxWidthPct}%`,
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {showOutline && (
        <>
          <Box
            sx={{
              position: 'absolute',
              inset: -4,
              border: `2px solid ${alpha(isSelected ? theme.palette.primary.main : theme.palette.common.white, 0.8)}`,
              borderRadius: '6px',
              pointerEvents: 'none',
            }}
          />
          {(isSelected || isResizing) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: -20,
                right: -4,
                backgroundColor: theme.palette.primary.main,
                color: '#fff',
                px: 0.75,
                py: 0.2,
                borderRadius: '3px',
                fontSize: '0.55rem',
                fontWeight: 700,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              w: {maxWidthPct}%
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              left: -4,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: isSelected ? theme.palette.primary.main : alpha(theme.palette.common.black, 0.6),
              color: '#fff',
              px: 1,
              py: 0.25,
              borderRadius: '4px',
              fontSize: '0.6rem',
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 11,
            }}
          >
            <OpenWithOutlined sx={{ fontSize: 10 }} />
            {ELEMENT_LABELS[elementKey]}
          </Box>
          {(isSelected || hovering) && (
            <Box
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onResizePointerDown(elementKey, e);
              }}
              sx={{
                position: 'absolute',
                right: -8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 12,
                height: 24,
                borderRadius: '4px',
                backgroundColor: theme.palette.primary.main,
                border: `2px solid ${theme.palette.background.paper}`,
                cursor: 'ew-resize',
                zIndex: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              <Box sx={{
                width: 2,
                height: 10,
                borderLeft: '1px solid rgba(255,255,255,0.5)',
                borderRight: '1px solid rgba(255,255,255,0.5)',
              }} />
            </Box>
          )}
        </>
      )}

      {children}
    </Box>
  );
};

type ToolbarProps = {
  elementKey: BannerElementKey;
  style: Required<ElementStyle>;
  onChange: (value: Partial<ElementStyle>) => void;
};

const ElementStyleToolbar: FC<ToolbarProps> = ({ elementKey, style, onChange }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        mt: 2,
        p: 5,
        borderRadius: '12px',
        border: `1px solid ${theme.palette.custom.gray[200]}`,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Stack direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 2 }}>
        <TextFieldsOutlined sx={{ fontSize: 16, color: theme.palette.primary.main }} />
        <Typography variant="textSM"
          sx={{ fontWeight: 600 }}>
          {ELEMENT_LABELS[elementKey]} Style
        </Typography>
      </Stack>

      <Stack spacing={3}>
        <Stack direction="row"
          spacing={2}
          alignItems="flex-start">
          <TextField
            select
            label="Font"
            value={style.fontFamily}
            onChange={(e) => onChange({ fontFamily: e.target.value })}
            size="small"
            sx={{ flex: 2, minWidth: 140 }}
          >
            {FONT_OPTIONS.map((f) => (
              <MenuItem key={f.value}
                value={f.value}
                sx={{ fontFamily: f.value || 'inherit' }}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>

          <Tooltip title="Bold">
            <IconButton
              size="small"
              onClick={() => onChange({ fontWeight: style.fontWeight >= 700 ? 400 : 700 })}
              sx={{
                border: `1px solid ${theme.palette.custom.gray[200]}`,
                backgroundColor: style.fontWeight >= 700 ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                mt: '8px !important',
              }}
            >
              <FormatBoldOutlined fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Italic">
            <IconButton
              size="small"
              onClick={() => onChange({ fontStyle: style.fontStyle === 'italic' ? 'normal' : 'italic' })}
              sx={{
                border: `1px solid ${theme.palette.custom.gray[200]}`,
                backgroundColor: style.fontStyle === 'italic' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                mt: '8px !important',
              }}
            >
              <FormatItalicOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box>
          <Stack direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}>
            <FormatSizeOutlined sx={{ fontSize: 14, color: theme.palette.custom.gray[500] }} />
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600] }}>
              Size: {style.fontSize}px
            </Typography>
          </Stack>
          <Slider
            value={style.fontSize}
            onChange={(_e, v) => onChange({ fontSize: v as number })}
            min={8}
            max={64}
            step={1}
            size="small"
            sx={{ mx: 1 }}
          />
        </Box>

        <Divider />

        <Stack direction="row"
          spacing={2}
          alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600], mb: 0.5 }}>
              Weight: {style.fontWeight}
            </Typography>
            <Slider
              value={style.fontWeight}
              onChange={(_e, v) => onChange({ fontWeight: v as number })}
              min={100}
              max={900}
              step={100}
              size="small"
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600], mb: 0.5 }}>
              Spacing: {style.letterSpacing}px
            </Typography>
            <Slider
              value={style.letterSpacing}
              onChange={(_e, v) => onChange({ letterSpacing: v as number })}
              min={-2}
              max={10}
              step={0.5}
              size="small"
            />
          </Box>
        </Stack>

        <Stack direction="row"
          spacing={1}>
          {TRANSFORM_OPTIONS.map((opt) => (
            <Box
              key={opt.value}
              onClick={() => onChange({ textTransform: opt.value })}
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: '6px',
                border: `1px solid ${style.textTransform === opt.value ? theme.palette.primary.main : theme.palette.custom.gray[200]}`,
                backgroundColor: style.textTransform === opt.value ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: style.textTransform === opt.value ? theme.palette.primary.main : theme.palette.custom.gray[600],
                userSelect: 'none',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              {opt.label}
            </Box>
          ))}
        </Stack>

        <Box>
          <Stack direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 0.5 }}>
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600] }}>
              Width: {style.maxWidth}%
            </Typography>
          </Stack>
          <Slider
            value={style.maxWidth}
            onChange={(_e, v) => onChange({ maxWidth: v as number })}
            min={10}
            max={100}
            step={5}
            size="small"
            sx={{ mx: 1 }}
          />
        </Box>

        <Divider />

        <Stack direction="row"
          spacing={2}
          alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600], mb: 0.5 }}>
              Padding: {style.padding}px
            </Typography>
            <Slider
              value={style.padding}
              onChange={(_e, v) => onChange({ padding: v as number })}
              min={0}
              max={40}
              step={1}
              size="small"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600], mb: 0.5 }}>
              Margin: {style.margin}px
            </Typography>
            <Slider
              value={style.margin}
              onChange={(_e, v) => onChange({ margin: v as number })}
              min={0}
              max={40}
              step={1}
              size="small"
            />
          </Box>
        </Stack>

        <Stack direction="row"
          spacing={2}
          alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600], mb: 0.5 }}>
              Height: {style.height || 'auto'}
            </Typography>
            <Slider
              value={style.height}
              onChange={(_e, v) => onChange({ height: v as number })}
              min={0}
              max={200}
              step={2}
              size="small"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600], mb: 0.5 }}>
              Width: {style.width || 'auto'}
            </Typography>
            <Slider
              value={style.width}
              onChange={(_e, v) => onChange({ width: v as number })}
              min={0}
              max={400}
              step={2}
              size="small"
            />
          </Box>
        </Stack>

        <Divider />

        <Stack direction="row"
          spacing={2}
          alignItems="center">
          <Typography variant="textXS"
            sx={{ color: theme.palette.custom.gray[600] }}>
            Text colour:
          </Typography>
          <Box
            component="label"
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              border: `1px solid ${theme.palette.custom.gray[300]}`,
              backgroundColor: style.color || '#fff',
              cursor: 'pointer',
              display: 'block',
              position: 'relative',
            }}
          >
            <input
              type="color"
              aria-label="Element color"
              value={style.color || '#ffffff'}
              onChange={(e) => onChange({ color: e.target.value })}
              style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
            />
          </Box>
          {style.color && (
            <Typography
              variant="textXS"
              onClick={() => onChange({ color: '' })}
              sx={{
                color: theme.palette.primary.main,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Reset
            </Typography>
          )}
        </Stack>

        {(elementKey === 'tag' || elementKey === 'cta') && (
          <Stack direction="row"
            spacing={2}
            alignItems="center">
            <Typography variant="textXS"
              sx={{ color: theme.palette.custom.gray[600] }}>
              Background:
            </Typography>
            <Box
              component="label"
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                border: `1px solid ${theme.palette.custom.gray[300]}`,
                backgroundColor: style.backgroundColor || (elementKey === 'tag' ? '#16A34A' : '#ffffff'),
                cursor: 'pointer',
                display: 'block',
                position: 'relative',
              }}
            >
              <input
                type="color"
                aria-label="Background color"
                value={style.backgroundColor || (elementKey === 'tag' ? '#16A34A' : '#ffffff')}
                onChange={(e) => onChange({ backgroundColor: e.target.value })}
                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
              />
            </Box>
            {style.backgroundColor && (
              <Typography
                variant="textXS"
                onClick={() => onChange({ backgroundColor: '' })}
                sx={{
                  color: theme.palette.primary.main,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Reset
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const DESIGN_WIDTH = BANNER_DESIGN_WIDTH;
const DESIGN_HEIGHT = BANNER_DESIGN_HEIGHT;

const DraggableBannerPreview: FC<Props> = ({
  data,
  layout,
  onLayoutChange,
  elementStyles,
  onElementStylesChange,
  aspectRatio,
}) => {
  const theme = useTheme();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0);

  const canvasHeight = aspectRatio ? Math.round(DESIGN_WIDTH / aspectRatio) : DESIGN_HEIGHT;

  const measureScale = useCallback(() => {
    if (!wrapperRef.current) return;
    const w = wrapperRef.current.clientWidth;
    setScale(Math.min(1, w / DESIGN_WIDTH));
  }, []);

  useLayoutEffect(() => {
    measureScale();
  }, [measureScale]);

  useEffect(() => {
    const ro = new ResizeObserver(measureScale);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [measureScale]);

  const [dragging, setDragging] = useState<BannerElementKey | null>(null);
  const [resizing, setResizing] = useState<BannerElementKey | null>(null);
  const [selected, setSelected] = useState<BannerElementKey | null>(null);
  const [hoveredKey, setHoveredKey] = useState<BannerElementKey | null>(null);

  const offsetRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({ pointerX: 0, startWidth: 0 });
  const pendingDragRef = useRef<BannerElementKey | null>(null);
  const pointerStartRef = useRef({ x: 0, y: 0 });

  const pos = (key: BannerElementKey): ElementPosition =>
    layout[key] ?? DEFAULT_LAYOUT[key];

  const style = (key: BannerElementKey) => resolveStyle(key, elementStyles);

  const color = data.textColor || '#fff';

  const textSx = (key: BannerElementKey, fallbackColor: string) => {
    const s = style(key);
    return {
      fontSize: `${s.fontSize}px`,
      fontWeight: s.fontWeight,
      ...(s.fontFamily ? { fontFamily: s.fontFamily } : {}),
      letterSpacing: s.letterSpacing ? `${s.letterSpacing}px` : undefined,
      textTransform: s.textTransform as React.CSSProperties['textTransform'],
      fontStyle: s.fontStyle,
      color: s.color || fallbackColor,
      lineHeight: 1.2,
      ...(s.padding ? { padding: `${s.padding}px` } : {}),
      ...(s.height ? { height: `${s.height}px` } : {}),
      ...(s.width ? { width: `${s.width}px` } : {}),
    };
  };

  const handlePointerDown = useCallback(
    (key: BannerElementKey, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget as HTMLElement;
      el.setPointerCapture(e.pointerId);

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const currentPos = pos(key);
      const elPixelX = (currentPos.x / 100) * DESIGN_WIDTH;
      const elPixelY = (currentPos.y / 100) * canvasHeight;
      const pointerX = (e.clientX - rect.left) / scale;
      const pointerY = (e.clientY - rect.top) / scale;

      offsetRef.current = {
        x: pointerX - elPixelX,
        y: pointerY - elPixelY,
      };

      pendingDragRef.current = key;
      pointerStartRef.current = { x: e.clientX, y: e.clientY };
    },
    [layout, scale, canvasHeight],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (resizing) {
        const delta = (e.clientX - resizeStartRef.current.pointerX) / scale;
        const deltaPct = (delta / DESIGN_WIDTH) * 100;
        const newWidth = Math.round(Math.max(10, Math.min(100, resizeStartRef.current.startWidth + deltaPct)));
        onElementStylesChange({
          ...elementStyles,
          [resizing]: { ...resolveStyle(resizing, elementStyles), maxWidth: newWidth },
        });
        return;
      }

      if (pendingDragRef.current && !dragging) {
        const dx = e.clientX - pointerStartRef.current.x;
        const dy = e.clientY - pointerStartRef.current.y;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
          setDragging(pendingDragRef.current);
        } else {
          return;
        }
      }

      if (!dragging) return;

      const pointerX = (e.clientX - rect.left) / scale - offsetRef.current.x;
      const pointerY = (e.clientY - rect.top) / scale - offsetRef.current.y;

      const x = Math.max(0, Math.min(95, (pointerX / DESIGN_WIDTH) * 100));
      const y = Math.max(0, Math.min(92, (pointerY / canvasHeight) * 100));

      onLayoutChange({
        ...layout,
        [dragging]: { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 },
      });
    },
    [dragging, resizing, scale, layout, onLayoutChange, elementStyles, onElementStylesChange, canvasHeight],
  );

  const handlePointerUp = useCallback(() => {
    pendingDragRef.current = null;
    setDragging(null);
    setResizing(null);
  }, []);

  const handleResizePointerDown = useCallback(
    (key: BannerElementKey, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget as HTMLElement;
      el.setPointerCapture(e.pointerId);

      resizeStartRef.current = {
        pointerX: e.clientX,
        startWidth: resolveStyle(key, elementStyles).maxWidth,
      };
      setResizing(key);
      setSelected(key);
    },
    [elementStyles],
  );

  const handleElementClick = useCallback((key: BannerElementKey) => {
    setSelected((prev) => (prev === key ? null : key));
  }, []);

  const handleCanvasClick = useCallback(() => {
    setSelected(null);
  }, []);

  const handleStyleChange = useCallback((patch: Partial<ElementStyle>) => {
    if (!selected) return;
    onElementStylesChange({
      ...elementStyles,
      [selected]: { ...resolveStyle(selected, elementStyles), ...patch },
    });
  }, [selected, elementStyles, onElementStylesChange]);

  const hasContent: Record<BannerElementKey, boolean> = {
    tag: !!data.tag,
    title: !!data.title,
    subtitle: !!data.subtitle,
    cta: !!data.ctaText,
  };

  return (
    <Box>
      <Box
        ref={wrapperRef}
        sx={{
          width: '100%',
          height: canvasHeight * scale,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: `${BANNER_CORNER_RADIUS}px`,
          backgroundColor: data.bgColor || '#1A1A2E',
          backgroundImage: data.imageUrl ? `url(${data.imageUrl})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color,
        }}
      >
        {data.imageUrl && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.10) 100%)',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}
        <Box
          ref={containerRef}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onClick={handleCanvasClick}
          sx={{
            width: DESIGN_WIDTH,
            height: canvasHeight,
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: `${BANNER_CORNER_RADIUS}px`,
            overflow: 'hidden',
            color,
            cursor: resizing ? 'ew-resize' : dragging ? 'grabbing' : 'default',
          }}
        >

          {!dragging && !hoveredKey && !selected && Object.values(hasContent).some(Boolean) && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: '8px',
                backgroundColor: alpha(theme.palette.common.black, 0.55),
                color: '#fff',
                fontSize: '0.65rem',
                fontWeight: 600,
                pointerEvents: 'none',
              }}
            >
              <OpenWithOutlined sx={{ fontSize: 12 }} />
            Drag to move · Click to style
            </Box>
          )}

          {dragging && (() => {
            const dp = pos(dragging);
            return (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: `${dp.y}%`,
                    height: '1px',
                    background: `repeating-linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.6)} 0 4px, transparent 4px 8px)`,
                    pointerEvents: 'none',
                    zIndex: 6,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${dp.x}%`,
                    width: '1px',
                    background: `repeating-linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.6)} 0 4px, transparent 4px 8px)`,
                    pointerEvents: 'none',
                    zIndex: 6,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: `${dp.y}%`,
                    left: 0,
                    transform: 'translateY(-100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    pointerEvents: 'none',
                    zIndex: 7,
                  }}
                >
                  <Box sx={{
                    width: `${dp.x}%`,
                    minWidth: 0,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  }} />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${dp.x}%`,
                    top: 0,
                    transform: 'translateX(-100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    pointerEvents: 'none',
                    zIndex: 7,
                  }}
                >
                  <Box sx={{
                    height: `calc(${dp.y}% * 3.2)`,
                    minHeight: 0,
                    borderRight: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                  }} />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${dp.x}%`,
                    top: `${dp.y}%`,
                    transform: 'translate(8px, -24px)',
                    backgroundColor: theme.palette.primary.main,
                    color: '#fff',
                    px: 1,
                    py: 0.25,
                    borderRadius: '4px',
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 8,
                  }}
                >
                x: {dp.x.toFixed(1)}% · y: {dp.y.toFixed(1)}%
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    backgroundColor: alpha('#fff', 0.12),
                    pointerEvents: 'none',
                    zIndex: 3,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '1px',
                    backgroundColor: alpha('#fff', 0.12),
                    pointerEvents: 'none',
                    zIndex: 3,
                  }}
                />
              </>
            );
          })()}

          {hasContent.tag && (
            <DragHandle
              elementKey="tag"
              position={pos('tag')}
              maxWidthPct={style('tag').maxWidth}
              isDragging={dragging === 'tag'}
              isSelected={selected === 'tag'}
              isResizing={resizing === 'tag'}
              hovering={hoveredKey === 'tag'}
              onPointerDown={handlePointerDown}
              onResizePointerDown={handleResizePointerDown}
              onClick={handleElementClick}
              onPointerEnter={() => setHoveredKey('tag')}
              onPointerLeave={() => setHoveredKey(null)}
            >
              <Chip
                label={data.tag}
                size="small"
                sx={{
                  background: `linear-gradient(90deg, ${style('tag').backgroundColor || data.tagColor || '#16A34A'} 0%, ${alpha(style('tag').backgroundColor || data.tagColor || '#16A34A', 0)} 100%)`,
                  color: style('tag').color || '#fff',
                  fontWeight: style('tag').fontWeight,
                  fontSize: `${style('tag').fontSize}px`,
                  ...(style('tag').fontFamily ? { fontFamily: style('tag').fontFamily } : {}),
                  letterSpacing: style('tag').letterSpacing ? `${style('tag').letterSpacing}px` : '0.04em',
                  textTransform: style('tag').textTransform,
                  height: style('tag').height || Math.max(22, style('tag').fontSize + 12),
                  ...(style('tag').width ? { width: `${style('tag').width}px` } : {}),
                  ...(style('tag').padding ? { padding: `${style('tag').padding}px` } : {}),
                  borderRadius: '6px',
                }}
              />
            </DragHandle>
          )}

          {hasContent.title && (
            <DragHandle
              elementKey="title"
              position={pos('title')}
              maxWidthPct={style('title').maxWidth}
              isDragging={dragging === 'title'}
              isSelected={selected === 'title'}
              isResizing={resizing === 'title'}
              hovering={hoveredKey === 'title'}
              onPointerDown={handlePointerDown}
              onResizePointerDown={handleResizePointerDown}
              onClick={handleElementClick}
              onPointerEnter={() => setHoveredKey('title')}
              onPointerLeave={() => setHoveredKey(null)}
            >
              <Typography
                component="div"
                sx={textSx('title', color)}
              >
                {data.title}
              </Typography>
            </DragHandle>
          )}

          {hasContent.subtitle && (
            <DragHandle
              elementKey="subtitle"
              position={pos('subtitle')}
              maxWidthPct={style('subtitle').maxWidth}
              isDragging={dragging === 'subtitle'}
              isSelected={selected === 'subtitle'}
              isResizing={resizing === 'subtitle'}
              hovering={hoveredKey === 'subtitle'}
              onPointerDown={handlePointerDown}
              onResizePointerDown={handleResizePointerDown}
              onClick={handleElementClick}
              onPointerEnter={() => setHoveredKey('subtitle')}
              onPointerLeave={() => setHoveredKey(null)}
            >
              <Typography
                component="div"
                sx={textSx('subtitle', alpha(color === '#fff' || color === '#FFFFFF' ? '#fff' : color, 0.85))}
              >
                {data.subtitle}
              </Typography>
            </DragHandle>
          )}

          {hasContent.cta && (
            <DragHandle
              elementKey="cta"
              position={pos('cta')}
              maxWidthPct={style('cta').maxWidth}
              isDragging={dragging === 'cta'}
              isSelected={selected === 'cta'}
              isResizing={resizing === 'cta'}
              hovering={hoveredKey === 'cta'}
              onPointerDown={handlePointerDown}
              onResizePointerDown={handleResizePointerDown}
              onClick={handleElementClick}
              onPointerEnter={() => setHoveredKey('cta')}
              onPointerLeave={() => setHoveredKey(null)}
            >
              <Chip
                label={data.ctaText}
                sx={{
                  backgroundColor: style('cta').backgroundColor || '#fff',
                  color: style('cta').color || '#1A1A2E',
                  fontWeight: style('cta').fontWeight,
                  fontSize: `${style('cta').fontSize}px`,
                  ...(style('cta').fontFamily ? { fontFamily: style('cta').fontFamily } : {}),
                  letterSpacing: style('cta').letterSpacing ? `${style('cta').letterSpacing}px` : '0.02em',
                  textTransform: style('cta').textTransform as React.CSSProperties['textTransform'],
                  fontStyle: style('cta').fontStyle,
                  height: style('cta').height || Math.max(32, style('cta').fontSize + 18),
                  ...(style('cta').width ? { width: `${style('cta').width}px` } : {}),
                  ...(style('cta').padding ? { padding: `${style('cta').padding}px` } : {}),
                  borderRadius: '8px',
                  px: 1.5,
                  '& .MuiChip-label': {
                    px: 1.5,
                  },
                }}
              />
            </DragHandle>
          )}
        </Box>
      </Box>

      {selected && hasContent[selected] && (
        <ElementStyleToolbar
          elementKey={selected}
          style={resolveStyle(selected, elementStyles)}
          onChange={handleStyleChange}
        />
      )}
    </Box>
  );
};

export default DraggableBannerPreview;
